import { uniqueId } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import useTouchedElement from './useTouchedElement';

interface IPrevElementProperties {
  position: string;
  zIndex: string;
}

interface Props {
  stickyElementId: string;
  stickyElementStaticContainerId: string;

  stickyClassName?: string;

  isKeepOriginPosition?: boolean;
  gap?: number;
}
/**
 * @description ie에서의 sticky 기능을 지원하기 위한 클래스입니다. isTouched를 반환합니다.
 */

export default function useSticky({
  stickyElementStaticContainerId,
  stickyElementId,
  stickyClassName = 'active-use-sticky',
  isKeepOriginPosition = true,
  gap = 0
}: Props) {
  const [fakeChildrenId] = useState<string>(uniqueId(`__fake_children_${Math.random()}__`));

  /**
   * @description 아이디 값이 스크롤의 탑에 닿으면 true를 반환합니다.
   */
  const { isTouched, calculateSticky } = useTouchedElement({
    elementId: stickyElementStaticContainerId,
    gap
  });

  const prevElementProperties = useRef<IPrevElementProperties | null>(null);

  const initialSettings = (findStickyElement: HTMLElement) => {
    if (isKeepOriginPosition) {
      const fakeChildrenElement = `<div id='${fakeChildrenId}'></div>`;

      findStickyElement.insertAdjacentHTML('afterbegin', fakeChildrenElement);

      const findFakeChildrenElement = document.getElementById(fakeChildrenId);

      if (findFakeChildrenElement) {
        findFakeChildrenElement.style.width = `${findStickyElement.clientWidth}px`;
        findFakeChildrenElement.style.height = `${findStickyElement.clientHeight}px`;
        findFakeChildrenElement.style.zIndex = '-999';
        findFakeChildrenElement.style.position = 'absolute';
      }
    }
  };

  const setSticky = (findStickyElement: HTMLElement) => {
    const findFakeChildrenElement = document.getElementById(fakeChildrenId);

    if (isKeepOriginPosition && findFakeChildrenElement) {
      findFakeChildrenElement.style.position = 'relative';
    }

    prevElementProperties.current = {
      position: findStickyElement.style.position,
      zIndex: findStickyElement.style.zIndex
    };

    findStickyElement.classList.add(stickyClassName);

    findStickyElement.style.position = 'fixed';
    findStickyElement.style.left = '0px';
    findStickyElement.style.right = '0px';
    findStickyElement.style.top = '0px';
    findStickyElement.style.zIndex = '190';
  };

  const clearSticky = (findStickyElement: HTMLElement) => {
    const findFakeChildrenElement = document.getElementById(fakeChildrenId);

    if (isKeepOriginPosition && findFakeChildrenElement) {
      findFakeChildrenElement.style.position = 'absolute';
    }

    findStickyElement.classList.remove(stickyClassName);

    findStickyElement.style.position = prevElementProperties.current?.position || 'relative';
    findStickyElement.style.zIndex = prevElementProperties.current?.zIndex || '1';
  };

  useEffect(() => {
    const findStickyContainerElement = document.getElementById(stickyElementStaticContainerId);

    if (findStickyContainerElement) {
      initialSettings(findStickyContainerElement);
    }
  }, []);

  useEffect(() => {
    const findStickyElement = stickyElementId && document.getElementById(stickyElementId);

    if (isTouched && findStickyElement) {
      setSticky(findStickyElement);
    } else if (!isTouched && findStickyElement) {
      clearSticky(findStickyElement);
    }
  }, [isTouched]);

  return { isTouched, clearSticky, calculateSticky };
}
